import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface ExecutiveKPI {
  id: string
  name: string
  category: string
  target: number
  actual: number
  unit: string
  achievement: number
  status: string
  trend: string
  yoy_change: number
}

interface Initiative {
  id: string
  name: string
  sponsor: string
  status: string
  priority: string
  budget: number
  spent: number
  progress_percent: number
  target_date: string
}

export default function ExecutiveDashboardPage() {
  const [kpis, setKPIs] = useState<ExecutiveKPI[]>([])
  const [initiatives, setInitiatives] = useState<Initiative[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [kpiRes, initRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/executive/kpis'),
      fetch(API_BASE + '/executive/initiatives'),
      fetch(API_BASE + '/executive/statistics')
    ])
    if (kpiRes.ok) setKPIs((await kpiRes.json()).kpis || [])
    if (initRes.ok) setInitiatives((await initRes.json()).initiatives || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'on_track': case 'completed': return 'bg-green-100 text-green-800'
      case 'at_risk': case 'delayed': return 'bg-yellow-100 text-yellow-800'
      case 'off_track': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-800'
      case 'high': return 'bg-orange-100 text-orange-800'
      default: return 'bg-blue-100 text-blue-800'
    }
  }

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up': return '↑'
      case 'down': return '↓'
      default: return '→'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Executive Dashboard</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Export Report</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Overall Health</div>
              <div className="text-2xl font-bold text-green-600">{statistics.overall_health_score}%</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">KPIs On Track</div>
              <div className="text-2xl font-bold text-green-600">{statistics.kpis_on_track}/{statistics.total_kpis}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Initiatives</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.initiatives_on_track}/{statistics.strategic_initiatives}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Budget Utilized</div>
              <div className="text-2xl font-bold">${(statistics.total_initiative_spent / 1000000).toFixed(1)}M</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Board Reports</div>
              <div className="text-2xl font-bold text-purple-600">{statistics.board_reports_ytd}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Executive KPIs</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">KPI</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Target vs Actual</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">YoY</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {kpis.map(kpi => (
                  <tr key={kpi.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-sm">{kpi.name}</div>
                      <div className="text-xs text-gray-500 capitalize">{kpi.category}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <span className="font-medium">{kpi.actual.toLocaleString()}</span>
                        <span className="text-gray-500"> / {kpi.target.toLocaleString()} {kpi.unit}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                        <div className={`h-1.5 rounded-full ${kpi.achievement >= 90 ? 'bg-green-500' : kpi.achievement >= 70 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{width: Math.min(kpi.achievement, 100) + '%'}}></div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className={`text-sm font-medium flex items-center ${kpi.yoy_change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {getTrendIcon(kpi.trend)} {kpi.yoy_change >= 0 ? '+' : ''}{kpi.yoy_change}%
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(kpi.status)}`}>{kpi.status.replace('_', ' ')}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Strategic Initiatives</h3>
            <div className="space-y-3">
              {initiatives.slice(0, 9).map(initiative => (
                <div key={initiative.id} className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-medium text-sm">{initiative.name}</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${getPriorityColor(initiative.priority)}`}>{initiative.priority}</span>
                  </div>
                  <div className="text-xs text-gray-500">{initiative.sponsor}</div>
                  <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                    <div className="bg-blue-600 h-1.5 rounded-full" style={{width: initiative.progress_percent + '%'}}></div>
                  </div>
                  <div className="flex justify-between items-center mt-1">
                    <span className={`text-xs ${getStatusColor(initiative.status).replace('bg-', 'text-').replace('-100', '-600')}`}>{initiative.progress_percent}% complete</span>
                    <span className="text-xs text-gray-500">Due: {initiative.target_date}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
